import {
  ConditionGroupLogic,
  isConditionGroup,
  isRelatedResourceCondition,
  type ConditionGroup,
  type ConditionGroupItem,
  type FieldCondition,
  type RelatedResourceCondition,
  type SegmentConditionGroups,
} from '@reach/shared-types/conditions';
import type { AvailableFieldsResponseWithTypes } from '../../engage-segments-tab/segment-definitions-api';
import type { BuilderFieldType } from './segment-builder.types';
import {
  isBuilderNestedGroup,
  isBuilderRelatedResourceCondition,
  type BuilderCondition,
  type BuilderFieldCondition,
  type BuilderGroup,
  type BuilderNestedGroup,
  type BuilderRelatedResourceCondition,
  type BuilderState,
} from './segment-builder.types';

function generateId(): string {
  return crypto.randomUUID();
}

/** Spread a description key only when it has a value, keeping stored JSON clean. */
function withDescription(description?: string): { description?: string } {
  return description ? { description } : {};
}

function fieldTypeFromSchemas(
  fieldName: string,
  contactFields: AvailableFieldsResponseWithTypes['contactFields'],
  relatedSchemas: AvailableFieldsResponseWithTypes['relatedSchemas'],
  relatedSchemaId?: string,
): BuilderFieldType {
  if (relatedSchemaId) {
    const schema = relatedSchemas.find(s => s.id === relatedSchemaId);
    const info = schema?.fields.find(f => f.name === fieldName);
    return info?.type ?? 'unknown';
  }
  const info = contactFields.find(f => f.name === fieldName);
  return info?.type ?? 'unknown';
}

/**
 * Recursively convert a condition group item (API format) to a builder condition.
 */
function conditionItemToBuilder(
  item: ConditionGroupItem,
  contactFields: AvailableFieldsResponseWithTypes['contactFields'],
  relatedSchemas: AvailableFieldsResponseWithTypes['relatedSchemas'],
): BuilderCondition {
  // Nested condition group -> BuilderNestedGroup
  if (isConditionGroup(item)) {
    const nestedConditions = item.conditions.map(child =>
      conditionItemToBuilder(child, contactFields, relatedSchemas),
    );
    return {
      id: generateId(),
      type: 'nested_group',
      logic: item.logic === ConditionGroupLogic.OR ? 'or' : 'and',
      conditions: nestedConditions,
      isExpanded: true,
      ...withDescription(item.description),
    } satisfies BuilderNestedGroup;
  }

  // Related resource condition -> BuilderRelatedResourceCondition
  if (isRelatedResourceCondition(item)) {
    const schema = relatedSchemas.find(s => s.id === item.relatedResource.schemaId);
    const builderNested: BuilderFieldCondition[] = item.conditions.map(fc => ({
      id: generateId(),
      type: 'field',
      field: typeof fc.field === 'string' ? fc.field : fc.field,
      fieldType: fieldTypeFromSchemas(
        typeof fc.field === 'string' ? fc.field : fc.field,
        contactFields,
        relatedSchemas,
        item.relatedResource.schemaId,
      ),
      operator: fc.operator,
      value: [...fc.value],
      ...withDescription(fc.description),
    }));
    return {
      id: generateId(),
      type: 'related_resource',
      schemaId: item.relatedResource.schemaId,
      schemaName: schema?.name ?? 'Related resource',
      foreignKeyPath: item.relatedResource.foreignKeyPath,
      countOperator: item.countOperator,
      countValue: item.countValue,
      conditions: builderNested,
      isExpanded: true,
      ...withDescription(item.description),
    } satisfies BuilderRelatedResourceCondition;
  }

  // Field condition -> BuilderFieldCondition
  const fieldCond = item as FieldCondition;
  const fieldName = typeof fieldCond.field === 'string' ? fieldCond.field : fieldCond.field;
  return {
    id: generateId(),
    type: 'field',
    field: fieldName,
    fieldType: fieldTypeFromSchemas(fieldName, contactFields, relatedSchemas),
    operator: fieldCond.operator,
    value: [...fieldCond.value],
    ...withDescription(fieldCond.description),
  } satisfies BuilderFieldCondition;
}

/**
 * Convert segment condition groups (API format) to builder state.
 * Fully supports nested condition groups via recursion.
 */
export function conditionGroupsToBuilderState(
  groups: SegmentConditionGroups,
  available: AvailableFieldsResponseWithTypes,
): BuilderState {
  const { contactFields, relatedSchemas } = available;

  const builderGroups: BuilderGroup[] = groups.map((group: ConditionGroup) => {
    const conditions = group.conditions.map(cond =>
      conditionItemToBuilder(cond, contactFields, relatedSchemas),
    );
    return {
      id: generateId(),
      logic: group.logic === ConditionGroupLogic.OR ? 'or' : 'and',
      conditions,
      ...withDescription(group.description),
    };
  });

  return { groups: builderGroups };
}

/**
 * Recursively convert a builder condition to API format.
 */
function builderConditionToApi(cond: BuilderCondition): ConditionGroupItem {
  // Nested group -> ConditionGroup
  if (isBuilderNestedGroup(cond)) {
    const nestedConditions = cond.conditions.map(builderConditionToApi);
    return {
      type: 'group',
      logic: cond.logic === 'or' ? ConditionGroupLogic.OR : ConditionGroupLogic.AND,
      conditions: nestedConditions,
      ...withDescription(cond.description),
    } as ConditionGroup;
  }

  // Related resource -> RelatedResourceCondition
  if (isBuilderRelatedResourceCondition(cond)) {
    return {
      type: 'related_resource',
      relatedResource: {
        schemaId: cond.schemaId,
        foreignKeyPath: cond.foreignKeyPath,
      },
      countOperator: cond.countOperator,
      countValue: cond.countValue,
      conditions: cond.conditions.map(fc => ({
        type: 'property' as const,
        field: fc.field,
        operator: fc.operator,
        value: [...fc.value],
        ...withDescription(fc.description),
      })),
      ...withDescription(cond.description),
    } satisfies RelatedResourceCondition;
  }

  // Field condition -> FieldCondition (type discriminant for condition group items)
  return {
    type: 'property',
    field: cond.field,
    operator: cond.operator,
    value: [...cond.value],
    ...withDescription(cond.description),
  } as FieldCondition;
}

/**
 * Convert builder state to segment condition groups (API format).
 * Fully supports nested condition groups via recursion.
 */
export function builderStateToConditionGroups(state: BuilderState): SegmentConditionGroups {
  return state.groups.map(group => {
    const conditions = group.conditions.map(builderConditionToApi);
    return {
      type: 'group' as const,
      logic: group.logic === 'or' ? ConditionGroupLogic.OR : ConditionGroupLogic.AND,
      conditions,
      ...withDescription(group.description),
    };
  });
}
